import { AiAssist } from "@aircall/react-icons";
import { useTranslation } from "react-i18next";
import { Navigate, Outlet, useLocation, useParams } from "react-router";
import { useAiLabelRule } from "@/hooks/useAiLabelRule/useAiLabelRule";
import { useBaseStore } from "@/store/store";
import { AiPromotionalBanner } from "../AiPromotionalBanner/AiPromotionalBanner";
import { TabMenu } from "../TabMenu/TabMenu";
import { SectionDetailsProps } from "./SectionDetails.decl";
import { useTabs } from "./SectionDetails.hooks";
import {
  isAssetPageTabItem,
  isEvaluationRoute,
  shouldHideTabMenu,
} from "./SectionDetails.utility";

function AiLabel() {
  const { t } = useTranslation();
  const { show } = useAiLabelRule();

  if (!show) {
    return null;
  }

  return (
    <div className="flex items-center gap-1 self-end m-4" data-test="ai-label">
      <span className="text-tiny font-medium">
        {t("call_context.powered_by_aircall_ai")}
      </span>
      <AiAssist className="size-3" />
    </div>
  );
}

export function SectionDetails({ mobile }: SectionDetailsProps) {
  const { pathname, search } = useLocation();
  const { callId, callType } = useParams();
  const isExpanded = useBaseStore((store) => store.isExpanded);
  const { tabs, disabledTabs, shouldRedirectInsights } = useTabs();

  if (!isAssetPageTabItem(pathname ?? "")) {
    return (
      <Navigate
        to={`/calls/${callId}/${callType}/${tabs.includes("insights") ? "insights" : "info"}${search}`}
        replace
      />
    );
  }

  if (
    shouldRedirectInsights &&
    callType !== "voicemail" &&
    pathname?.endsWith("/insights")
  ) {
    return (
      <Navigate to={`/calls/${callId}/${callType}/info${search}`} replace />
    );
  }

  const shouldHideTabs = shouldHideTabMenu(pathname);
  const shrink = !mobile && isExpanded;

  return (
    <div
      className={`flex flex-1 flex-col bg-card [overflow:initial] [scrollbar-gutter:stable] [transition:opacity_0.25s_ease-in-out,margin_0.3s_ease-in-out,padding_0.3s_ease-in-out] ${
        shrink || mobile
          ? "min-w-0"
          : "min-w-[576px] min-[768px]:max-[1200px]:min-w-[50%]"
      } ${
        shrink
          ? "m-0 opacity-0 rounded-2xl border-none"
          : "m-3 opacity-100 rounded-2xl border border-solid border-border"
      }`}
      data-test="details-section"
    >
      <div
        className="flex flex-1 flex-col overflow-hidden"
        data-test="details-section-inner"
      >
        {!shouldHideTabs && !!tabs.length && (
          <div className="p-4">
            <TabMenu tabs={tabs} disabledTabs={disabledTabs} />
          </div>
        )}
        <div
          className={`flex flex-col gap-6 h-full overflow-auto pb-6 ${
            isEvaluationRoute(pathname) ? "px-0" : "px-6"
          }`}
        >
          <Outlet />
          <div hidden={shrink} className="shrink-0 empty:hidden">
            <AiPromotionalBanner key={callId} />
          </div>
        </div>
        <div className="flex flex-1 flex-col-reverse">
          <AiLabel />
        </div>
      </div>
    </div>
  );
}
